import React from 'react';
import {UseFormRegister, UseFormWatch} from 'react-hook-form';
import cn from 'classnames';
import {Trans} from '@lingui/react';
import {t} from '@lingui/core/macro';

import FormGroup from 'shared/ui/FormGroup/FormGroup';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import {HintPremium} from 'entities/tariff';
import IHttpMonitorOutput from 'features/http/model/HttpMonitor/IHttpMonitor';
import {
    getAvailableHttpMethods,
    getCurrentHttpMethod,
    HTTP_PREMIUM_METHODS,
} from 'features/http/lib/httpRequestMethodView';
import {isTranslate} from 'shared/lib/lang';

import styles from './RequestMethod.module.scss';

interface IExtraSettingsSubformProps {
    item?: IHttpMonitorOutput;
    register: UseFormRegister<any>;
    watch: UseFormWatch<any>;
    isFreeTariff: boolean | null;
}

const RequestMethod: React.FC<IExtraSettingsSubformProps> = ({item, register, watch, isFreeTariff}) => {
    const currentMethod = getCurrentHttpMethod(watch('method'), item?.method);
    const availableMethods = getAvailableHttpMethods(isFreeTariff, isTranslate());

    const METHOD_DESCRIPTIONS = {
        HEAD: t`HEAD — запрашиваются только заголовки. Тело ответа не загружается и не сохраняется.`,
        // eslint-disable-next-line max-len
        GET: t`GET — запрашиваются заголовки и тело. При инциденте содержимое сохраняется на 30 дней. Доступны опции: разбор проблем и проверка контента.`,
        // eslint-disable-next-line max-len
        POST: t`POST — отправляется запрос с пустым телом. Заголовки и тело сохраняются при инциденте на 30 дней. Доступны опции: разбор проблем и проверка контента.`,
    } as const;

    return (
        <FormGroup className={styles.formGroup}>
            <label className={styles.label}>
                <Trans id="requestMethod.title" message="Метод отправки запроса"/>
            </label>
            <div className={cn(
                styles.radioGroup,
                styles.colorsRadioGroup,
            )}>
                {availableMethods.map((method) => {
                    const isDisabled = isTranslate() && method !== 'HEAD';

                    return (
                        <label
                            key={method}
                            className={cn(
                                styles.radioOption,
                                currentMethod === method && styles.active,
                            )}
                        >
                            <input
                                type="radio"
                                value={method}
                                checked={currentMethod === method}
                                {...register('method')}
                                disabled={isDisabled}
                            />
                            <div className={styles.name}>
                                {method}
                            </div>
                            {currentMethod === method && (
                                <HintQuestion
                                    placement="top-start"
                                    className={styles.hintWrapper}
                                >
                                    {METHOD_DESCRIPTIONS[method as keyof typeof METHOD_DESCRIPTIONS]}
                                </HintQuestion>
                            )}
                            {currentMethod !== method && (
                                <div className={styles.wrapperQuestion}>
                                    <HintQuestion placement="top-start">
                                        <div>
                                            <div>
                                                {/* eslint-disable-next-line max-len */}
                                                {isTranslate() && 'This feature is not available yet. We are working on it and plan to release it soon.'}
                                            </div>
                                            <div className={styles.spacer}/>
                                            {METHOD_DESCRIPTIONS[method as keyof typeof METHOD_DESCRIPTIONS]}
                                        </div>
                                    </HintQuestion>
                                </div>
                            )}
                        </label>
                    );
                })}

                {isFreeTariff && !isTranslate() && HTTP_PREMIUM_METHODS.map((method) => (
                    <div key={method} className={styles.itemHelps}>
                        <HintPremium condition={'monitor_extended_methods'}/>
                        <span className={styles.text}>{method}</span>
                        <HintQuestion
                            placement="top-start"
                            className={styles.hintWrapper}
                        >
                            {METHOD_DESCRIPTIONS[method as keyof typeof METHOD_DESCRIPTIONS]}
                        </HintQuestion>
                    </div>
                ))}
            </div>
        </FormGroup>
    );
};

export default RequestMethod;